import { MessageCircleOff, Pencil, UserPlus } from '@/components/ui/icons';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useParams } from 'react-router';
import { LIMITS, type ConversationDTO } from '@clanfire/shared';
import { conversationApi } from '@/api/endpoints';
import { ChatView } from '@/components/chat/ChatView';
import { RightPanel, TopBar } from '@/components/layout/TopBar';
import { SearchPanel } from '@/components/search/SearchPanel';
import { GroupMembers } from '@/components/social/GroupMembers';
import { Avatar, GroupAvatar, statusLabel } from '@/components/ui/Avatar';
import { Button, IconButton } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/Feedback';
import { Spinner } from '@/components/ui/Spinner';
import { conversationTitle, recipientsOf, renameGroup } from '@/lib/conversations';
import { useConversations } from '@/stores/social';
import { useUi } from '@/stores/ui';
import { usePresenceOf, useUsers } from '@/stores/users';
import { PinsButton } from '@/components/chat/PinsButton';

export function ConversationPage() {
  const { conversationId = '' } = useParams();
  const conversation = useConversations((s) => s.conversations[conversationId]);
  const [state, setState] = useState<'ready' | 'loading' | 'missing'>(conversation ? 'ready' : 'loading');
  const users = useUsers((s) => s.users);
  const recipients = conversation ? recipientsOf(conversation, users) : [];
  const isGroup = conversation?.type === 'GROUP';
  const recipient = isGroup ? null : (recipients[0] ?? null);
  const presence = usePresenceOf(recipient?.id);
  const searchOpen = useUi((s) => s.searchOpen);
  const setSearchOpen = useUi((s) => s.setSearchOpen);
  const membersOpen = useUi((s) => s.membersOpen);
  const setMembersOpen = useUi((s) => s.setMembersOpen);
  const openModal = useUi((s) => s.openModal);

  // A DM link can be opened before the conversation is in the list (e.g. it was closed).
  useEffect(() => {
    if (conversation) {
      setState('ready');
      return;
    }
    let cancelled = false;
    setState('loading');
    conversationApi
      .get(conversationId)
      .then((c) => !cancelled && useConversations.getState().upsert(c))
      .catch(() => !cancelled && setState('missing'));
    return () => {
      cancelled = true;
    };
  }, [conversationId, conversation]);

  if (state === 'missing') {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          icon={<MessageCircleOff size={28} />}
          title="Conversation not found"
          action={
            <Link to="/app/@me">
              <Button>Back to friends</Button>
            </Link>
          }
        />
      </div>
    );
  }
  if (!conversation || (!isGroup && !recipient)) {
    return (
      <div className="flex h-full items-center justify-center text-fg-muted">
        <Spinner />
      </div>
    );
  }

  const groupFull = recipients.length + 1 >= LIMITS.groupDmMaxMembers;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TopBar
        icon={
          isGroup ? (
            <GroupAvatar users={recipients} size={28} ring="ring-surface" />
          ) : (
            <button type="button" onClick={() => openModal({ type: 'profile', userId: recipient!.id })} aria-label={`View ${recipient!.displayName}'s profile`}>
              <Avatar user={recipient} size={26} status={presence} ring="ring-surface" />
            </button>
          )
        }
        title={isGroup ? <GroupName conversation={conversation} /> : recipient!.displayName}
        subtitle={isGroup ? `${recipients.length + 1} members` : (recipient!.customStatus ?? statusLabel[presence])}
        showMembersToggle={isGroup}
      >
        <IconButton
          label={isGroup ? (groupFull ? 'This group is full' : 'Add friends to the group') : 'Start a group with them'}
          disabled={isGroup && groupFull}
          onClick={() => openModal({ type: 'newDm', conversationId })}
        >
          <UserPlus size={20} />
        </IconButton>
        <PinsButton target={{ conversationId }} canPin />
      </TopBar>
      <div className="flex min-h-0 flex-1">
        <ChatView key={conversationId} target={{ conversationId }} className="min-w-0 flex-1" />
        {searchOpen ? (
          <RightPanel onClose={() => setSearchOpen(false)}>
            <SearchPanel scope={{ conversationId }} onClose={() => setSearchOpen(false)} />
          </RightPanel>
        ) : (
          isGroup &&
          membersOpen && (
            <RightPanel onClose={() => setMembersOpen(false)}>
              <GroupMembers conversation={conversation} onClose={() => setMembersOpen(false)} />
            </RightPanel>
          )
        )}
      </div>
    </div>
  );
}

/** A group's name in the header; click it to rename the group. */
function GroupName({ conversation }: { conversation: ConversationDTO }) {
  const users = useUsers((s) => s.users);
  const title = conversationTitle(conversation, users);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) input.current?.select();
  }, [editing]);

  const save = async () => {
    if (value.trim() === (conversation.name ?? '')) return setEditing(false);
    setSaving(true);
    const ok = await renameGroup(conversation, value);
    setSaving(false);
    if (ok) setEditing(false);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={input}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => void save()}
        disabled={saving}
        maxLength={LIMITS.groupNameMax}
        placeholder={conversationTitle({ ...conversation, name: null }, users)}
        aria-label="Group name"
        className="h-8 w-full max-w-72 rounded-lg border border-accent bg-sunken px-2 font-sans text-base text-fg focus:outline-none"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        setValue(conversation.name ?? '');
        setEditing(true);
      }}
      title="Rename group"
      className="group/name flex max-w-full items-center gap-1.5 truncate rounded-lg text-left hover:text-fg-soft"
    >
      <span className="truncate">{title}</span>
      <Pencil size={14} className="shrink-0 text-fg-muted opacity-0 transition-opacity group-hover/name:opacity-100" aria-hidden />
      <span className="sr-only">(rename)</span>
    </button>
  );
}
